import { Tree } from "@minoru/react-dnd-treeview";
import {
	DragLayerMonitorProps,
	NodeModel,
} from "@minoru/react-dnd-treeview/dist/types";
import { Alert } from "@mui/lab";
import {
	Grid2,
	Paper,
	Snackbar,
	SnackbarCloseReason,
	Typography,
} from "@mui/material";
import React, {
	MouseEvent as ReactMouseEvent,
	useEffect,
	useMemo,
	useState,
} from "react";
import { ErrorModal } from "../../../../components/modals/error-modal";
import { LazyLoading } from "../../../../controls/LazyLoading/LazyLoading";
import { useContextMenuMESF } from "../../../../controls/contextMenu";
import { HelmetDexteel } from "../../../../helmet/HelmetDexteel";
import { Asset } from "../../../../models/Asset";
import { TransformAssetModelData } from "../hooks/TransformAssetModelData";
import { useAssetActions } from "../hooks/useAssetActions";
import { AssetSearchResult } from "../hooks/useAssetSearch";
import { useSearchAssets } from "../hooks/useSearchAssets";
import { AssetParameters } from "../models/AssetParameters";
import { FileProperties } from "../models/FileProperties";
import { AssetSearchInput } from "./AssetSearchInput";
import { CreateNewAssetDialog } from "./dialogs/create-new-asset-dialog";
import { EditAssetDialog } from "./dialogs/edit-asset-dialog";
import { RemoveAssetDialog } from "./dialogs/remove-asset-dialog";
import { ViewerAssetDialog } from "./dialogs/viewer-asset-dialog";
import { CustomDragPreview } from "./drag/CustomDragPreview";
import { MultipleDragPreview } from "./drag/MultipleDragPreview";
import { useAssetOptionsFunctions } from "./hooks/useAssetOptionsFunctions";
import { CustomNode } from "./node/CustomNode";

// Re-export for backward compatibility
export { useSearchAssets as searchAssets } from "../hooks/useSearchAssets";

export const TreeAsset = () => {
	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const [selectedNodes, setSelectedNodes] = useState<
		NodeModel<FileProperties>[]
	>([]);
	const [isDragging, setIsDragging] = useState(false);
	const [isCtrlPressing, setIsCtrlPressing] = useState(false);

	const [showDialog, setShowDialog] = useState<
		"view" | "new" | "delete" | "edit" | ""
	>("");

	const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
	const [openAssets, setOpenAssets] = useState<{
		[assetId: number | string]: number | string;
	}>({});
	const [allAssets, setAllAssets] = useState<AssetParameters[]>([]);
	const [assetNodes, setAssetNodes] = useState<AssetParameters[]>([]);

	const [assetNodeSelectedInTree, setAssetNodeSelectedInTree] =
		useState<AssetParameters | null>(null);

	const [error, setError] = useState<string>("");
	const [updatedSuccessfullyMessage, setUpdatedSuccessfullyMessage] =
		useState(false);

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		isSuccess,
		isFetching,
		refetch,
	} = useSearchAssets();

	const { getMenuOptions } = useAssetOptionsFunctions({
		setAssetNodeSelectedInTree,
		setAssetId: setSelectedAssetId,
		setShowModal: setShowDialog,
		handleMoveUp: () => {},
		handleMoveDown: () => {},
	});

	const { handleDrop, handleCtrlClick, handleDragStart, handleDragEnd } =
		useAssetActions({
			asset: assetNodes as NodeModel<FileProperties>[],
			selectedNodes,
			setSelectedNodes,
			setIsDragging,
			isCtrlPressing,
			setIsCtrlPressing,
			onAssetUpdateStart: () => {},
			onAssetUpdateEnd: (result) => {
				if (result.ok) {
					setUpdatedSuccessfullyMessage(true);
					refetch();
				} else {
					setError(result.message);
				}
			},
		});

	const handleClose = (
		event: React.SyntheticEvent<any> | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setUpdatedSuccessfullyMessage(false);
	};

	const handleContextMenu = (
		data: NodeModel<FileProperties>,
		e: ReactMouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		if (e.type === "contextmenu") {
			setSelectedNodes([data]);
			showContextMenu(e, data, "assetContext");
			const selectedNode = allAssets.find(
				(node: AssetParameters) => node.id === data.id,
			);
			if (selectedNode) {
				setAssetNodeSelectedInTree(selectedNode);
			}
		}
	};

	const handleDoubleClick = (
		data: NodeModel<FileProperties>,
		e: React.MouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		const selectedNode = allAssets.find((node: any) => node.id === data.id);
		if (selectedNode) {
			setAssetNodeSelectedInTree(selectedNode);
			setSelectedAssetId(selectedNode.id);
			setShowDialog("view");
		}
	};

	const handleSearchResultClick = (result: AssetSearchResult) => {
		const expandPath = (nodeId: number): number[] => {
			const path: number[] = [];
			const findNodeAndPath = (currentId: number): void => {
				const node = assetNodes.find((n) => n.id === currentId);
				if (node && node.parent !== -1) {
					path.unshift(currentId);
					findNodeAndPath(node.parent as number);
				} else if (node) {
					path.unshift(currentId);
				}
			};
			findNodeAndPath(nodeId);
			return path;
		};

		const pathToExpand = expandPath(result.id);

		const newOpenAssets = { ...openAssets };
		pathToExpand.forEach((nodeId) => {
			newOpenAssets[nodeId] = nodeId;
		});

		setOpenAssets(newOpenAssets);

		setSelectedNodes([result.node]);
		setTimeout(() => {
			const nodeElement = document.querySelector(
				`[data-node-id="${result.id}"]`,
			);
			if (nodeElement) {
				nodeElement.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});
			}
		}, 300);
	};

	const openAssetsArray = useMemo(() => {
		return Object.keys(openAssets).map((el) => Number.parseInt(el));
	}, [openAssets]);

	useEffect(() => {
		if (isSuccess) {
			let mydataSource: any[] = [];

			const all: AssetParameters[] = rows.map((row: Asset) => {
				return {
					id: row.AssetId,
					parent: row.ParentAssetId || 0,
					text: row.AssetName,
					isAtTop: false,
					isAtBottom: false,
					isEditable: true,
					sortOrder: 0 /*row.SortOrder*/,
					data: {
						fileType: row.IsLeaf ? "file" : "folder",
						fileSize: "0",
					},
					droppable: !row.IsLeaf,
					isOpen: false,
				};
			});

			setAllAssets(all);

			rows.forEach((row: Asset) => {
				const code = TransformAssetModelData(row);
				mydataSource = [...mydataSource, code];
			});

			setAssetNodes(mydataSource);
		}
	}, [isSuccess, isFetching]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	useEffect(() => {
		registerConfig({
			id: "assetContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<HelmetDexteel title="Assets Tree" />
			<>
				<Grid2
					container
					justifyContent="flex-start"
					onContextMenu={(e) => showContextMenu(e, null, "assetContext")}
					sx={{ width: "100%", height: "100%" }}
					p={1}
					spacing={1}
				>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Typography variant="h5" fontWeight={600}>
							Assets Tree
						</Typography>
					</Grid2>
					{isLoading ? (
						<Grid2 container style={{ width: "100%", height: "100%" }}>
							<Grid2
								size={{ xs: 12, md: 12 }}
								style={{
									height: "71vh",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<div
									style={{
										width: "100%",
										height: "100%",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<LazyLoading />
								</div>
							</Grid2>
						</Grid2>
					) : (
						<Grid2 size={{ md: 12, xs: 12 }}>
							<Paper
								elevation={2}
								sx={{
									width: "100%",
									borderRadius: "0.75rem",
									padding: 2,
								}}
							>
								<AssetSearchInput
									assetNodes={assetNodes as NodeModel<FileProperties>[]}
									onSearchResultClick={handleSearchResultClick}
								/>
								<Grid2 container alignItems="center">
									<Grid2
										container
										sx={{
											height: "68vh",
											overflowY: "auto",
											paddingBottom: 10,
											"& .root": {
												boxSizing: "border-box",
												height: "100%",
												padding: 2,
											},
											"& .dropTarget": {
												boxShadow: "0 0 0 1px #1967d2 inset",
											},
										}}
									>
										<Tree
											rootId={-1}
											initialOpen={openAssetsArray}
											tree={assetNodes as NodeModel<FileProperties>[]}
											enableAnimateExpand={true}
											classes={{
												root: "root",
												dropTarget: "dropTarget",
											}}
											onDrop={handleDrop}
											onDragStart={handleDragStart}
											onDragEnd={handleDragEnd}
											canDrop={(asset: any, options: any): boolean => {
												return !selectedNodes.some(
													(selectedNode) =>
														selectedNode.id === options.dropTargetId,
												);
											}}
											render={(node, options) => {
												const selected = selectedNodes.some(
													(selectedNode) => selectedNode.id === node.id,
												);

												return (
													<CustomNode
														{...options}
														node={node}
														openAssets={openAssets}
														setOpenAssets={setOpenAssets}
														setContextMenuOver={handleContextMenu}
														isSelected={selected}
														isDragging={selected && isDragging}
														onClick={handleCtrlClick}
														onDoubleClick={handleDoubleClick}
													/>
												);
											}}
											dragPreviewRender={(
												monitorProps: DragLayerMonitorProps<FileProperties>,
											) => {
												if (selectedNodes.length > 1) {
													return (
														<MultipleDragPreview dragSources={selectedNodes} />
													);
												}

												return (
													<CustomDragPreview monitorProps={monitorProps} />
												);
											}}
										/>
									</Grid2>
								</Grid2>
							</Paper>
						</Grid2>
					)}
				</Grid2>
				<Snackbar
					open={updatedSuccessfullyMessage}
					autoHideDuration={2500}
					onClose={handleClose}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Alert severity="success" onClose={handleClose}>
						Asset updated successfully
					</Alert>
				</Snackbar>
				<CreateNewAssetDialog
					assetNodeSelectedInTree={assetNodeSelectedInTree}
					show={showDialog === "new"}
					onHide={async (shouldUpdate) => {
						setShowDialog("");
						if (shouldUpdate) {
							refetch();
						}
					}}
					suffixTitle="Assets Tree"
				/>
				<EditAssetDialog
					selectedAssetId={selectedAssetId}
					show={showDialog === "edit"}
					onHide={async (shouldUpdate) => {
						setShowDialog("");
						if (shouldUpdate) {
							await refetch();
						}
					}}
					suffixTitle="Assets Tree"
				/>
				<ViewerAssetDialog
					selectedAssetId={selectedAssetId}
					show={showDialog === "view"}
					onHide={() => setShowDialog("")}
					suffixTitle="Assets Tree"
				/>
				<RemoveAssetDialog
					selectedAssetId={selectedAssetId}
					selectedAssetName={assetNodeSelectedInTree?.text ?? ""}
					show={showDialog === "delete"}
					onHide={async (shouldUpdate) => {
						setShowDialog("");
						if (shouldUpdate) {
							await refetch();
						}
					}}
					suffixTitle="Assets Tree"
				/>
				<ErrorModal error={error} onHide={() => setError("")} />
			</>
		</>
	);
};
